import { useMemo, useState, useEffect } from 'react';

import Badge from "@mui/material/Badge";

import { paths } from 'src/routes/paths';

import { useTranslate } from 'src/locales';

import SvgColor from 'src/components/svg-color';

import Iconify from "../../components/iconify";
import { useAuthContext } from "../../auth/hooks";
import { hasTimesheetClockings } from "../../config-global";
import EmployeeService from "../../services/EmployeeService";
import { isModuleEnabled } from "../../utils/company-modules";
import { DOCUMENTS_CHANGED_EVENT } from "../../utils/documents-events";
import { hasSupervisorPermission } from "../../utils/check-permission";

// ----------------------------------------------------------------------

const icon = (name) => (
  <SvgColor src={`/assets/icons/navbar/${name}.svg`} sx={{ width: 1, height: 1 }}/>
  // OR
  // <Iconify icon="fluent:mail-24-filled" />
  // https://icon-sets.iconify.design/solar/
  // https://www.streamlinehq.com/icons
);

const ICONS = {
  job: icon('ic_job'),
  timesheet: icon('ic_blog'),
  chat: icon('ic_chat'),
  mail: icon('ic_mail'),
  user: icon('ic_user'),
  file: icon('ic_file'),
  lock: icon('ic_lock'),
  tour: icon('ic_tour'),
  order: icon('ic_order'),
  label: icon('ic_label'),
  blank: icon('ic_blank'),
  kanban: icon('ic_kanban'),
  folder: icon('ic_folder'),
  banking: icon('ic_banking'),
  booking: icon('ic_booking'),
  invoice: icon('ic_invoice'),
  product: icon('ic_product'),
  calendar: icon('ic_calendar'),
  disabled: icon('ic_disabled'),
  external: icon('ic_external'),
  menuItem: icon('ic_menu_item'),
  ecommerce: icon('ic_ecommerce'),
  analytics: icon('ic_analytics'),
  dashboard: icon('ic_dashboard'),
};

// ----------------------------------------------------------------------

export function useNavData() {
  const { t, i18n } = useTranslate();
  const { user } = useAuthContext();

  const [links, setLinks] = useState([]);
  const [pendingCount, setPendingCount] = useState(0);

  const fetchLinks = async () => {
    const res = await EmployeeService.getExternalLinks();
    setLinks(res.data);
  }

  const fetchDocumentAlertCount = async () => {
    try {
      const [pendingRes, signRes] = await Promise.all([
        EmployeeService.getPendingDocuments(),
        EmployeeService.getDocumentsToSign(),
      ]);
      const pending = (pendingRes.data || []).length;
      const toSign = (signRes.data?.data || []).length;
      setPendingCount(pending + toSign);
    } catch {
      // silent
    }
  }

  const MENU_ITEMS = useMemo(() => ({
    documents: {
      title: t('documents.title'),
      path: paths.dashboard.documents,
      icon: pendingCount > 0
        ? <Badge badgeContent={pendingCount} color="error" max={9} sx={{ display: 'block', width: 1, height: 1 }}>{ICONS.file}</Badge>
        : ICONS.file,
    },
    calendar: {
      title: t('calendar'),
      path: paths.dashboard.root,
      icon: ICONS.calendar,
    },
    richieste: {
      title: t('richieste'),
      path: paths.dashboard.requests,
      icon: ICONS.booking,
    },
    yearlyPlanning: {
      // textTransform:none così la "e" resta minuscola (il menu di default applica capitalize)
      title: <span style={{ textTransform: 'none' }}>Piano ferie & smart working</span>,
      path: paths.dashboard.yearlyPlanning,
      icon: <Iconify icon="solar:calendar-mark-bold-duotone" sx={{ width: 24, height: 24 }}/>,
    },
    clocking: {
      title: t('clocking'),
      path: paths.dashboard.clocking,
      icon: ICONS.job,
    },
    expense_reports: {
      title: t('expense_reports'),
      path: paths.dashboard.expenseReports,
      icon: ICONS.invoice,
    },
    notices: {
      title: t('notices'),
      path: paths.dashboard.notices,
      icon: ICONS.mail,
    },
    dati_contabili: {
      title: t('dati contabili'),
      path: paths.dashboard.payrollData,
      icon: <Iconify icon="solar:calculator-bold-duotone" sx={{ width: 24, height: 24 }}/>,
    }
  }), [t, i18n.language, pendingCount]);

  const userMenuItems = useMemo(() => ([
    ...(isModuleEnabled(user?.data?.company, 'feature_documents') ? [MENU_ITEMS.documents] : []),
    ...((user?.data?.has_timesheet && user?.data?.company?.feature_employee_calendar) ? [MENU_ITEMS.calendar] : []),
    ...((user?.data?.has_timesheet && user?.data?.company?.feature_requests) ? [MENU_ITEMS.richieste] : []),
    ...((user?.data?.has_timesheet && isModuleEnabled(user?.data?.company, 'feature_timesheet') && hasTimesheetClockings(user?.data?.timesheet_type)) ? [MENU_ITEMS.clocking] : []),
    ...(user?.data?.company?.feature_expense_reports ? [MENU_ITEMS.expense_reports] : []),
    ...((user?.data?.has_timesheet && user?.data?.company?.feature_holiday_plan) ? [MENU_ITEMS.yearlyPlanning] : []),
    ...(isModuleEnabled(user?.data?.company, 'feature_communications') ? [MENU_ITEMS.notices] : []),
    ...(isModuleEnabled(user?.data?.company, 'feature_payroll_data') ? [MENU_ITEMS.dati_contabili] : []),
  ]), [user, MENU_ITEMS]);

  useEffect(() => {
    if (user) {
      fetchLinks().then();
      fetchDocumentAlertCount().then();
    }
  }, [user]);

  // Badge Documenti aggiornato subito dopo firma o compilazione, senza attendere il prossimo login.
  useEffect(() => {
    const refresh = () => { fetchDocumentAlertCount().then(); };
    window.addEventListener(DOCUMENTS_CHANGED_EVENT, refresh);
    return () => window.removeEventListener(DOCUMENTS_CHANGED_EVENT, refresh);
  }, []);

  const data = useMemo(
    () => [
      // DESKTOP
      // ----------------------------------------------------------------------
      {
        items: userMenuItems.slice(0, 5),
      },

      // PROFILE
      // ----------------------------------------------------------------------
      ...((userMenuItems.length > 5 || links.length > 0) ? [
        {
          subheader: t('profile'),
          items: [
            ...userMenuItems.slice(5),
            ...(user && links.length > 0 ? [{
              title: t('external_services'),
              path: '#/external',
              icon: ICONS.external,
              children: links.map((link) => (
                {
                  title: link.description,
                  path: link.url.startsWith('http') ? link.url : `https://${link.url}`,
                  externalLink: true,
                }
              )),
            }] : []),
          ]
        },
      ] : []),

      // COMPANY
      // ----------------------------------------------------------------------
      ...(hasSupervisorPermission(user, 'requests_view') && user?.data?.company?.feature_requests
        && user?.data?.company?.feature_webapp_team_requests !== false ? [{
        subheader: t('company'),
        items: [
          {
            title: t('team_requests'),
            path: paths.dashboard.approveRequests,
            icon: <Iconify icon="solar:calendar-search-bold-duotone"/>,
          },
        ]
      }] : []),


    ],
    [t, user, links, userMenuItems, i18n.language]
  );

  return data;
}
